import { useEffect, useImperativeHandle, useMemo, useState, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiError, PERMISSION, apiUrl, can, contactAttributes, fillQuickReply, inboxApi, useAuth,
  type CannedReply, type QuickReplyContext,
} from '@wa/shared';

/** A quick reply's file, handed to the composer to send with the text. */
export interface QuickReplyAttachment {
  mediaAssetId: number;
  fileName: string;
  mimeType: string | null;
}

function attachmentOf(reply: CannedReply): QuickReplyAttachment | null {
  return reply.mediaAssetId
    ? { mediaAssetId: reply.mediaAssetId, fileName: reply.mediaFileName ?? 'file', mimeType: reply.mediaMimeType }
    : null;
}

interface Props {
  /** The text after the leading slash, or null when the panel is closed. */
  query: string | null;
  contactName: string | null;
  /** For {{field.key}}; the contact is fetched only while the panel is open. */
  contactId?: number | null;
  /** The current draft, offered for saving when an admin has typed something. */
  draft: string;
  onPick: (text: string, attachment: QuickReplyAttachment | null) => void;
  onClose: () => void;
  /** Keyboard control from the composer, which owns focus. */
  ref?: Ref<QuickReplyKeys>;
}

/**
 * What the composer's textarea can do to the panel without taking its focus:
 * move the highlight, and pick what is highlighted. `pick` answers whether it
 * took the Enter — false when there is nothing to pick, so the composer sends.
 */
export interface QuickReplyKeys {
  move(delta: number): void;
  pick(): boolean;
}

/**
 * The list that opens when an agent types `/`.
 *
 * Saving lives here rather than in a settings screen: the moment anyone wants to
 * keep a reply is just after typing it for the third time, and that moment
 * happens in the composer.
 */
export function QuickReplyPanel({ query, contactName, contactId, draft, onPick, onClose, ref }: Props) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [shortcut, setShortcut] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [highlight, setHighlight] = useState(0);
  // "All" or only the ones that carry a file — the two a person reaches for
  // ("send the colour disclaimer" is a picture; "address" is text).
  const [tab, setTab] = useState<'all' | 'files'>('all');

  const open = query !== null;
  // The permission the route actually checks, not a role name a tenant may have renamed.
  const isAdmin = can(user, PERMISSION.cannedWrite);

  const replies = useQuery({
    queryKey: ['canned-replies'],
    queryFn: inboxApi.cannedReplies,
    staleTime: 300_000,
    enabled: open,
  });

  // The contact's custom fields, for {{field.key}}. Only fetched while the
  // panel is open and only when some reply actually uses a field.
  const usesFields = (replies.data ?? []).some((r) => /\{\{\s*field\./i.test(r.body));
  const contact = useQuery({
    queryKey: ['contact', contactId],
    queryFn: () => inboxApi.contact(contactId!),
    staleTime: 60_000,
    enabled: open && usesFields && typeof contactId === 'number',
  });

  const agentName = user?.displayName ?? null;
  const context = useMemo<QuickReplyContext>(() => ({
    contactName,
    agentName,
    fields: contact.data ? contactAttributes(contact.data) : null,
  }), [contactName, agentName, contact.data]);

  const save = useMutation({
    mutationFn: () => inboxApi.createCannedReply(shortcut, draft.replace(/^\/\S*\s*/, '')),
    onSuccess: () => {
      setShortcut('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['canned-replies'] });
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not save it.'),
  });

  const remove = useMutation({
    mutationFn: inboxApi.deleteCannedReply,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['canned-replies'] }),
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not delete it.'),
  });

  const matches = useMemo(() => {
    const all = (replies.data ?? []).filter((r) => tab === 'all' || r.mediaAssetId !== null);
    const needle = (query ?? '').trim().toLowerCase();
    if (!needle) return all;

    return all.filter(
      (r) => r.shortcut.includes(needle) || r.body.toLowerCase().includes(needle));
  }, [replies.data, query, tab]);

  // Typing narrows the list; the highlight goes back to the top so Enter always
  // picks the row that is drawn as chosen.
  useEffect(() => { setHighlight(0); }, [query, tab]);

  useImperativeHandle(ref, () => ({
    move(delta) {
      if (matches.length === 0) return;
      setHighlight((h) => (h + delta + matches.length) % matches.length);
    },
    pick() {
      const reply = matches[Math.min(highlight, matches.length - 1)];
      if (!reply) return false;
      onPick(fillQuickReply(reply.body, context), attachmentOf(reply));
      return true;
    },
  }), [matches, highlight, onPick, context]);

  if (!open) return null;

  function pick(reply: CannedReply) {
    onPick(fillQuickReply(reply.body, context), attachmentOf(reply));
  }

  const withFiles = (replies.data ?? []).filter((r) => r.mediaAssetId !== null).length;

  return (
    <div className="absolute bottom-full left-0 z-20 mb-2 w-full">
      <div className="overflow-hidden rounded-xl border border-(--color-line) bg-(--color-surface) shadow-(--shadow-pop)">
        {/* Tabs the way a chat tool draws them, with the pencil to manage the
            list at the right. Esc closes; the hint sits where the eye lands. */}
        <div className="flex items-center gap-1 border-b border-(--color-line) px-2">
          {([['all', 'All replies'], ['files', `With files${withFiles ? ` · ${withFiles}` : ''}`]] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              aria-pressed={tab === key}
              className={`tap -mb-px border-b-2 px-3 py-2 text-small font-medium transition-colors ${
                tab === key ? 'border-(--color-brand) text-(--color-brand)' : 'border-transparent text-(--color-muted) hover:text-(--color-ink)'
              }`}
            >
              {label}
            </button>
          ))}
          <span className="ml-auto hidden text-micro text-(--color-faint) sm:inline">↑↓ to move · Enter to use</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close quick replies"
            className="tap ml-1 rounded px-1.5 py-1 text-micro text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
          >
            Esc
          </button>
          {isAdmin && (
            <Link
              to="/settings/quick-replies"
              title="Manage quick replies"
              aria-label="Manage quick replies"
              className="tap ml-1 grid size-8 place-items-center rounded-md text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)"
            >
              <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                <path d="M3.5 14.5 13 5l2 2-9.5 9.5H3.5z M12 6l2 2" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
              </svg>
            </Link>
          )}
        </div>

        <ul className="max-h-72 overflow-y-auto">
          {replies.isPending && (
            <li className="px-3 py-4 text-center text-small text-(--color-muted)">Loading…</li>
          )}

          {replies.isSuccess && matches.length === 0 && (
            <li className="px-3 py-5 text-center text-small text-(--color-muted)">
              {replies.data.length === 0
                ? 'No quick replies yet. Save one from a draft below, or under Settings → Quick replies.'
                : tab === 'files' && !query
                  ? 'None of the quick replies carry a file.'
                  : `Nothing matches "${query}".`}
            </li>
          )}

          {matches.map((reply, index) => {
            const isImage = (reply.mediaMimeType ?? '').startsWith('image/');
            const thumb = reply.mediaUrl ? apiUrl(reply.mediaUrl) : null;
            return (
              <li key={reply.id} className="border-b border-(--color-line-dim) last:border-b-0">
                <div
                  className={`flex items-center gap-3 px-3 py-2 ${index === highlight ? 'bg-(--color-sunken)' : ''}`}
                  onMouseEnter={() => setHighlight(index)}
                >
                  <button
                    type="button"
                    onClick={() => pick(reply)}
                    aria-selected={index === highlight}
                    className="tap min-w-0 flex-1 text-left"
                  >
                    <span className="flex items-center gap-2">
                      <span className="truncate text-small font-semibold text-(--color-ink)">{reply.shortcut.replace(/[_-]+/g, ' ')}</span>
                      <span className="shrink-0 font-mono text-micro text-(--color-muted)">/{reply.shortcut}</span>
                      {reply.mediaAssetId && !isImage && (
                        <span className="shrink-0 rounded bg-(--color-line-dim) px-1.5 text-micro text-(--color-muted)">
                          {reply.mediaFileName ?? 'file'}
                        </span>
                      )}
                    </span>
                    {/* No `block`: line-clamp needs display:-webkit-box. */}
                    <span className="mt-0.5 line-clamp-2 text-small text-(--color-muted)">
                      {fillQuickReply(reply.body, context) || (reply.mediaAssetId ? 'Sends the file with no text' : '')}
                    </span>
                  </button>

                  {reply.mediaAssetId && (
                    isImage && thumb
                      ? <img src={thumb} alt="" className="size-11 shrink-0 rounded-md border border-(--color-line) object-cover" loading="lazy" />
                      : (
                        <span className="grid size-11 shrink-0 place-items-center rounded-md bg-(--color-sunken) text-(--color-muted)" aria-hidden="true">
                          <svg viewBox="0 0 20 20" width="18" height="18"><path d="M6 3h6l4 4v10H6z M12 3v4h4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>
                        </span>
                      )
                  )}

                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => remove.mutate(reply.id)}
                      aria-label={`Delete the /${reply.shortcut} quick reply`}
                      className="tap shrink-0 rounded p-1.5 text-(--color-faint) hover:text-(--color-bad)"
                    >
                      <svg viewBox="0 0 20 20" width="13" height="13" aria-hidden="true">
                        <path d="M5 5l10 10M15 5L5 15" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {/* Offered only when there is something worth keeping — an admin who has
            typed a reply is one field away from never typing it again. */}
        {isAdmin && draft.replace(/^\/\S*\s*/, '').trim().length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-t border-(--color-line) bg-(--color-sunken) px-3 py-2">
            <span className="text-micro text-(--color-muted)">Save this draft as</span>
            <span className="font-mono text-small text-(--color-muted)">/</span>
            <input
              value={shortcut}
              onChange={(e) => setShortcut(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
              placeholder="shortcut"
              aria-label="Shortcut for the new quick reply"
              className="tap w-28 min-w-0 rounded border border-(--color-line) bg-(--color-surface) px-2 py-1 font-mono text-small outline-none focus:border-(--color-brand)"
            />
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!shortcut || save.isPending}
              className="tap rounded bg-(--color-brand) px-2.5 py-1 text-small font-medium text-(--color-brand-ink) disabled:opacity-40"
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        {error && (
          <p role="alert" className="border-t border-(--color-line) px-3 py-2 text-micro text-(--color-bad)">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
